import { ReactNode } from 'react'

import { StatCard } from '@percent/lemonade'

import { MetricData } from '../../types/insights.types'
import { InsightsColourMap } from '../../utils/colourResolver'
import { LegendItem, metricHasData, orderByKeys } from '../../utils/metricView'
import { ChartBodyProps, TableLabels } from '../charts/chartBodies'
import { InsightsCardBody } from '../InsightsCardBody/InsightsCardBody'
import { InsightsCardWrapper } from '../InsightsCardWrapper/InsightsCardWrapper'
import { InsightsLegend } from '../InsightsLegend/InsightsLegend'

/**
 * A chart type's explicit behaviour: which data it can render, its legend, and its body.
 * Each typed component (`Insights.BarChart`, …) supplies one of these, so the rendering rules
 * live with the chart — not in a central switch. Teams can define a custom chart by pairing a
 * new spec with a renderer.
 */
export type ChartSpec = {
  supports: (data: MetricData) => boolean
  /**
   * The chart's legend. `options` carries what a dual-axis chart needs to work out
   * which series it can plot — and so which the legend must mark as not plotted:
   * the host's axis override, plus the domain's series order, whose tie-breaking
   * must match the chart body's. Charts on a single scale can ignore both.
   */
  legend: (
    data: MetricData,
    colourMap: InsightsColourMap,
    options: {
      rightAxisSeries?: readonly string[]
      seriesOrder?: readonly string[]
    }
  ) => LegendItem[]
  /**
   * Caption for the legend, when the chart's legend enumerates *rows* — the row
   * axis names them ("Country"), the same string the table heads its key column
   * with. Omit on a chart whose legend enumerates series: those are named by the
   * chart's own value axes, and heading them with the row axis would misdescribe
   * them. `undefined` (no axis in the response, or none that fits) heads nothing.
   */
  legendHeading?: (data: MetricData) => string | undefined
  renderBody: (props: ChartBodyProps) => ReactNode
}

/** The StatCard trend badge shape (mirrors Lemonade's `StatCardTrend`). */
export type MetricTrendBadge = {
  value?: string
  direction: 'up' | 'down'
  tooltip?: string
}

export type MetricRendererProps = {
  /** The metric data to render; `undefined` while a cold fetch is in flight. */
  data?: MetricData
  /** Which chart the data renders as (its `supports`/`legend`/`renderBody`). */
  spec: ChartSpec
  colourMap: InsightsColourMap
  seriesOrder: string[]
  /** StatCard frame. */
  label: string
  value?: string
  subtitle?: string
  description?: string
  trend?: MetricTrendBadge
  showLegend?: boolean
  /** Fetch lifecycle — all default to the "settled, no error" state for a data-in-hand render. */
  error?: unknown
  isLoading?: boolean
  isShowingPlaceholder?: boolean
  /** Chart body options, forwarded to `spec.renderBody`. */
  height?: number
  /** BCP 47 locale for the chart's axis ticks and tooltip values; defaults to `en-US`. */
  locale?: string
  orientation?: 'vertical' | 'horizontal'
  wrapXAxisLabels?: boolean
  yAxisWidth?: number
  innerRadiusRatio?: number
  rightAxisSeries?: string[]
  /** Table body: translated Value/Total/Share column headers. */
  tableLabels?: TableLabels
  /**
   * Pre-translated hover copy for a legend entry the chart couldn't plot (its unit
   * had no axis), e.g. "Not shown: this figure's unit needs a third axis".
   */
  notShownTooltip?: string
  emptyState?: ReactNode
  errorLabel?: string
  testId: string
}

/**
 * The presentational core shared by `MetricView` (fetch-by-slug) and `Insights.MetricChart`
 * (data-in-hand): given a resolved `MetricData` plus a `spec`, it frames the value/trend in a
 * `StatCard`, renders the chart body, its legend, and the loading / empty / incompatible states.
 * It never fetches — the caller supplies the data and lifecycle.
 */
export function MetricRenderer({
  data,
  spec,
  colourMap,
  seriesOrder,
  label,
  value,
  subtitle,
  description,
  trend,
  showLegend = true,
  error,
  isLoading = false,
  isShowingPlaceholder = false,
  height,
  locale,
  orientation,
  wrapXAxisLabels,
  yAxisWidth,
  innerRadiusRatio,
  rightAxisSeries,
  tableLabels,
  notShownTooltip,
  emptyState,
  errorLabel,
  testId,
}: Readonly<MetricRendererProps>) {
  const supported = data ? spec.supports(data) : true
  const incompatible = Boolean(data) && !supported

  const legendData =
    showLegend && data && supported
      ? orderByKeys(
          spec.legend(data, colourMap, { rightAxisSeries, seriesOrder }),
          seriesOrder,
          (item) => item.key
        ).map((item) =>
          item.muted && notShownTooltip
            ? { ...item, tooltip: notShownTooltip }
            : item
        )
      : []
  const legend =
    legendData.length > 0 ? (
      <InsightsLegend
        items={legendData}
        heading={data ? spec.legendHeading?.(data) : undefined}
        testId={`${testId}-legend`}
      />
    ) : undefined

  const hasData = data && supported ? metricHasData(data) : false

  const chartBody =
    data && supported
      ? spec.renderBody({
          data,
          colourMap,
          seriesOrder,
          height,
          locale,
          orientation,
          wrapXAxisLabels,
          yAxisWidth,
          innerRadiusRatio,
          rightAxisSeries,
          tableLabels,
        })
      : null

  // A scalar metric renders no chart body (`renderBody` → null). Only mount the
  // body area when there's something to show — a chart, or a loading / error /
  // empty / incompatible state (all of which imply `!hasData`). Omitting it for a
  // settled scalar lets StatCard drop its chart-slot gap, so a KPI card ends flush
  // at the subtitle instead of carrying an empty 16px gap below it (NEX-793).
  const showBody = !hasData || chartBody != null

  return (
    <InsightsCardWrapper
      isShowingPlaceholder={isShowingPlaceholder}
      testId={testId}
    >
      <StatCard
        label={label}
        value={value}
        subtitle={subtitle}
        description={description}
        trend={trend}
        legend={legend}
      >
        {showBody ? (
          <InsightsCardBody
            error={error}
            isLoading={isLoading}
            hasData={hasData}
            incompatible={incompatible}
            testId={testId}
            height={height}
            errorLabel={errorLabel}
            emptyState={emptyState}
          >
            {chartBody}
          </InsightsCardBody>
        ) : null}
      </StatCard>
    </InsightsCardWrapper>
  )
}
